import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { getQuizResultsForAdmin } from '@/services/quizGameService';
import { useDebounce } from '@/hooks/useDebounce';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { 
  Trophy, 
  Clock, 
  Search, 
  Download, 
  Calendar, 
  Filter, 
  Users,
  CheckCircle,
  XCircle,
  Eye,
  FileText,
  RefreshCw,
  BarChart2,
  ChevronDown,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

const QuizResultsAdminPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Trạng thái
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedResults, setSelectedResults] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // Bộ lọc
  const [filters, setFilters] = useState({
    userId: '',
    gameId: '',
    startDate: '',
    endDate: '',
    minScore: 0,
    maxScore: 100
  });
  
  // Debounce bộ lọc
  const debouncedFilters = useDebounce(filters, 500);
  
  // Số lượng kết quả mỗi trang
  const ITEMS_PER_PAGE = 20;
  
  useEffect(() => {
    loadResults();
  }, [currentPage, debouncedFilters]);
  
  const loadResults = async () => {
    setLoading(true);
    
    try {
      const resultData = await getQuizResultsForAdmin(
        debouncedFilters, 
        ITEMS_PER_PAGE, 
        currentPage
      );
      
      console.log('Quiz results loaded:', resultData);
      
      setResults(resultData.results || []);
      setTotalPages(resultData.totalPages || 1);
      setTotalRecords(resultData.total || 0);
    } catch (error) {
      console.error('Error loading quiz results:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset về trang đầu khi lọc thay đổi
  };
  
  const resetFilters = () => {
    setFilters({
      userId: '',
      gameId: '',
      startDate: '',
      endDate: '',
      minScore: 0,
      maxScore: 100
    });
    setCurrentPage(1);
  };
  
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: vi });
    } catch (e) {
      return dateString;
    }
  };
  
  const formatTime = (seconds: number) => {
    if (!seconds) return '0:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  const getScoreClass = (score: number) => {
    if (score >= 90) return 'bg-emerald-50 text-emerald-700';
    if (score >= 70) return 'bg-blue-50 text-blue-700';
    if (score >= 50) return 'bg-amber-50 text-amber-700';
    return 'bg-red-50 text-red-700';
  };
  
  const exportToCSV = () => {
    // Chuẩn bị dữ liệu xuất
    const header = ['ID', 'User', 'Quiz', 'Score', 'Correct', 'Total', 'Time (s)', 'Completed At'];
    
    const csvRows = [
      header.join(','),
      ...results.map(result => [
        result.id,
        result.user?.username || result.userId,
        result.game?.title || result.gameId,
        result.score,
        result.correctAnswers,
        result.totalQuestions,
        result.timeSpent,
        result.completedAt
      ].join(','))
    ];
    
    // Tạo blob và download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `quiz_results_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleBulkAction = (action: string) => {
    if (action === 'export' && selectedResults.length > 0) {
      // Xuất chỉ các kết quả đã chọn
      console.log('Export selected results:', selectedResults);
      // TODO: implement export selected
    }
  };
  
  const handleResultSelect = (id: string, checked: boolean) => {
    setSelectedResults(prev => 
      checked 
        ? [...prev, id] 
        : prev.filter(resultId => resultId !== id)
    );
  };
  
  const selectAllResults = (checked: boolean) => {
    setSelectedResults(checked ? results.map(r => r.id) : []);
  };
  
  return (
    <div className="container py-6 px-4 md:px-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Quản lý kết quả Quiz</h1>
          <p className="text-muted-foreground">
            Xem và phân tích kết quả từ các người dùng
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="mr-2 h-4 w-4" />
                Xuất dữ liệu
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Xuất dữ liệu</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={exportToCSV}>
                <FileText className="mr-2 h-4 w-4" />
                Xuất tất cả kết quả (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleBulkAction('export')}
                disabled={selectedResults.length === 0}
              >
                <FileText className="mr-2 h-4 w-4" />
                Xuất {selectedResults.length} kết quả đã chọn
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <SheetTrigger asChild>
              <Button variant="outline">
                <Filter className="mr-2 h-4 w-4" />
                Bộ lọc
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>Lọc kết quả Quiz</SheetTitle>
                <SheetDescription>
                  Điều chỉnh các bộ lọc để tìm kết quả cụ thể
                </SheetDescription>
              </SheetHeader>
              
              <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">ID người dùng</label>
                  <Input 
                    placeholder="Nhập ID người dùng" 
                    value={filters.userId} 
                    onChange={e => handleFilterChange('userId', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">ID quiz game</label>
                  <Input 
                    placeholder="Nhập ID quiz game" 
                    value={filters.gameId} 
                    onChange={e => handleFilterChange('gameId', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Từ ngày</label>
                  <Input 
                    type="date"
                    value={filters.startDate} 
                    onChange={e => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Đến ngày</label>
                  <Input 
                    type="date"
                    value={filters.endDate} 
                    onChange={e => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <label className="text-sm font-medium">Điểm số (min-max)</label>
                    <span className="text-sm text-muted-foreground">
                      {filters.minScore} - {filters.maxScore}
                    </span>
                  </div>
                  <div className="pt-4">
                    <div className="flex space-x-4">
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={filters.minScore} 
                        onChange={e => handleFilterChange('minScore', Number(e.target.value))}
                        className="w-20"
                      />
                      <Slider
                        defaultValue={[0, 100]}
                        value={[filters.minScore, filters.maxScore]}
                        max={100}
                        step={1}
                        onValueChange={([min, max]) => {
                          setFilters(prev => ({
                            ...prev,
                            minScore: min,
                            maxScore: max
                          }));
                        }}
                        className="flex-1"
                      />
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={filters.maxScore} 
                        onChange={e => handleFilterChange('maxScore', Number(e.target.value))}
                        className="w-20"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <SheetFooter>
                <Button variant="outline" onClick={resetFilters}>
                  Đặt lại bộ lọc
                </Button>
                <Button onClick={() => setIsFilterOpen(false)}>
                  Áp dụng bộ lọc
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
          
          <Button variant="default" onClick={loadResults}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Làm mới
          </Button>
        </div>
      </div>
      
      {/* Thông tin tóm tắt */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="mr-4 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-700" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tổng kết quả</p>
                <p className="text-2xl font-bold">{totalRecords}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="mr-4 h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tỷ lệ đạt</p>
                <p className="text-2xl font-bold">
                  {results.filter(r => r.score >= 50).length} / {results.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="mr-4 h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Trophy className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Điểm trung bình</p>
                <p className="text-2xl font-bold">
                  {results.length > 0 
                    ? Math.round(results.reduce((acc, r) => acc + r.score, 0) / results.length) 
                    : 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center">
              <div className="mr-4 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <BarChart2 className="h-5 w-5 text-purple-700" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Thời gian TB</p>
                <p className="text-2xl font-bold">
                  {results.length > 0 
                    ? formatTime(Math.round(results.reduce((acc, r) => acc + r.timeSpent, 0) / results.length))
                    : '0:00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Bảng kết quả */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Kết quả Quiz</CardTitle>
            <p className="text-sm text-muted-foreground">
              Hiển thị {results.length} / {totalRecords} kết quả
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <p>{t("loading", { ns: "common" })}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Không có kết quả nào phù hợp với tiêu chí tìm kiếm.</p>
              <Button variant="outline" className="mt-4" onClick={resetFilters}>
                Đặt lại bộ lọc
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedResults.length === results.length && results.length > 0}
                        onCheckedChange={selectAllResults}
                      />
                    </TableHead>
                    <TableHead>Người dùng</TableHead>
                    <TableHead>Quiz</TableHead>
                    <TableHead className="text-center">Điểm</TableHead>
                    <TableHead className="text-right">Đúng/Tổng</TableHead>
                    <TableHead className="text-right">Thời gian</TableHead>
                    <TableHead className="text-right">Hoàn thành</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((result) => (
                    <TableRow key={result.id}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedResults.includes(result.id)}
                          onCheckedChange={(checked: boolean) => handleResultSelect(result.id, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {result.user?.username || 'N/A'}
                          <span className="text-xs text-muted-foreground">
                            {result.userId.substring(0, 8)}...
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{result.game?.title || 'N/A'}</div>
                        <div className="text-xs text-muted-foreground">
                          {result.gameId.substring(0, 8)}...
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={getScoreClass(result.score)}>
                          {result.score}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {result.correctAnswers}/{result.totalQuestions}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatTime(result.timeSpent)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatDate(result.completedAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/admin/quiz/results/${result.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {totalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 py-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage > 1) setCurrentPage(currentPage - 1);
                      }}
                    />
                  </PaginationItem>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Logic để hiển thị các trang xung quanh trang hiện tại
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink 
                          href="#" 
                          isActive={pageNum === currentPage}
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(pageNum);
                          }}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}
                  
                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationLink 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage(totalPages);
                        }}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )}
                  
                  <PaginationItem>
                    <PaginationNext 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuizResultsAdminPage; 